import React, { useMemo, useState, useEffect, useRef } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Typography, CircularProgress, Box, Alert, Checkbox, Button, Autocomplete,
    TextField, TablePagination, Tooltip, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
    Divider, List, ListItem, ListItemText,
} from "@mui/material";
import ProcessSelectedModal from "./ProcessSelectedModal";
import FacilityServiceDetailsModal from "./FacilityServiceDetailsModal";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";

// Icons
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import BlockIcon from "@mui/icons-material/Block";

import { useAuth } from "../components/TokenHandler";




const SERVICE_OPTIONS = ["RPM Passive", "RPM Active", "CCM", "BHI"];
const ALL_SERVICE_HEADERS = SERVICE_OPTIONS;

const formatIdList = (arr, max = 12) => {
    if (!Array.isArray(arr) || arr.length === 0) return "none";
    const clean = arr
        .map((x) => (x == null ? null : String(x).trim()))
        .filter(Boolean);

    if (clean.length <= max) return clean.join(", ");
    const shown = clean.slice(0, max).join(", ");
    return `${shown}, … (+${clean.length - max})`;
};

const buildCorpChangeTooltip = (pc) => {
    if (!pc) return "Possible corp change";

    // Most important: show facility_unique_ids first
    const facilityIds = formatIdList(pc.matched_facility_unique_ids, 20);

    const bits = [
        `Facility IDs: ${facilityIds}`,
        pc.building_unique_id != null ? `Building ID: ${pc.building_unique_id}` : null,
        pc.pcc_record_count != null ? `PCC records: ${pc.pcc_record_count}` : null,

        // Optional (kept minimal): show counts only, not long lists
        Array.isArray(pc.corp_unique_ids) && pc.corp_unique_ids.length
            ? `Corp IDs: ${pc.corp_unique_ids.length}`
            : null,
        Array.isArray(pc.ouids) && pc.ouids.length
            ? `OUIDs: ${pc.ouids.length}`
            : null,
    ].filter(Boolean);

    return bits.join(" · ");
};


const buildDeactivatedApiTooltip = (da) => {
    if (!da) return "Deactivated API";
    const bits = [
        da.source_system ? `Source: ${da.source_system}` : null,
        da.account_status ? `Status: ${da.account_status}` : null,
        da.facility_unique_id != null ? `ID: ${da.facility_unique_id}` : null,
    ].filter(Boolean);

    return bits.length ? bits.join(" · ") : "Deactivated API";
};



/** ---------------------- ReportIssueModal (multi-facility) ---------------------- */
function ReportIssueModal({ open, onClose, facilities = [], excluded = [], onSubmitted }) {

    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();

    const [title, setTitle] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setTitle("");
            setNotes("");
            setError("");
        }
    }, [open]);

    const titleOptions = ["duplicate facility", "corp change", "other"];
    const normalizeTitle = (t) => (t === "duplicate facility" ? "duplicate" : t);


    const handleSubmit = async () => {
        setError("");
        if (!facilities.length) {
            setError("No eligible facilities selected.");
            return;
        }
        if (!title) {
            setError("Please select a title.");
            return;
        }
        if (notes.length > 128) {
            setError("Notes must be 128 characters or fewer.");
            return;
        }

        const items = facilities.map((f) => ({
            facility_unique_id: f.facility_unique_id,
            facility_name: f.facility_name,
            action: "create",
            title: normalizeTitle(title),
            notes: notes || "",
            hide: false,        // per requirements
            forward: false,     // per requirements
            forward_to: null,
        }));

        try {
            setSubmitting(true);

            const url = `${process.env.REACT_APP_API_URL}/facility/exclusions/batch`;

            await axios.post(
                url,
                { items },
                {
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                    },
                }
            );

            setSubmitting(false);
            onSubmitted?.();
            onClose();
        } catch (e) {
            setSubmitting(false);
            setError(e?.response?.data?.message || e?.message || "Failed to submit issues.");
        }
    };



    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Report Issue</DialogTitle>
            <DialogContent dividers>
                {!!excluded.length && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        The following facilities already have an issue and were excluded:
                        <ul style={{ marginTop: 8 }}>
                            {excluded.map((f) => (
                                <li key={f.facility_unique_id}>
                                    {f.facility_name} (ID: {f.facility_unique_id})
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Stack spacing={2}>
                    <Typography variant="subtitle2">Facilities to file issues for:</Typography>
                    <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: "auto" }}>
                        <List dense>
                            {facilities.map((f) => (
                                <ListItem key={f.facility_unique_id} disableGutters>
                                    <ListItemText
                                        primary={`${f.facility_name}`}
                                        secondary={`ID: ${f.facility_unique_id}`}
                                    />
                                </ListItem>
                            ))}
                            {!facilities.length && (
                                <ListItem disableGutters>
                                    <ListItemText primary="(None eligible)" />
                                </ListItem>
                            )}
                        </List>
                    </Paper>

                    <Divider />

                    <FormControl fullWidth>
                        <InputLabel id="issue-title-label">Title</InputLabel>
                        <Select
                            labelId="issue-title-label"
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        >
                            {titleOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Notes (max 128 chars)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value.slice(0, 128))}
                        helperText={`${notes.length}/128`}
                        multiline
                        minRows={2}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || facilities.length === 0}
                    startIcon={<ReportProblemOutlinedIcon />}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** ---------------------- Main Component ---------------------- */
const FacilityManagementView = ({
                                    facilities,
                                    loading,
                                    error,
                                    reloadFacilities,

                                    services = [],
                                    onChangeServices,
                                    date = "",
                                    onChangeDate,
                                    onApply = () => {},

                                    pagination = { total: 0, page: 1, pageSize: 50, totalPages: 0 },
                                    onPageChange,
                                    onPageSizeChange,
                                }) => {
    const [pendingServices, setPendingServices] = useState(services || []);
    const [pendingDateStr, setPendingDateStr] = useState(date || "");

    const arraysEqual = (a = [], b = []) => a.length === b.length && a.every((v, i) => v === b[i]);

    useEffect(() => {
        if (!arraysEqual(services, pendingServices)) setPendingServices(services || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [services]);

    useEffect(() => {
        const next = date || "";
        if (next !== pendingDateStr) setPendingDateStr(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const firstRun = useRef(true);
    useEffect(() => {
        if (firstRun.current) { firstRun.current = false; return; }
        const t = setTimeout(() => {
            onChangeServices?.(pendingServices);
            onChangeDate?.(pendingDateStr);
            onApply?.();
        }, 500);
        return () => clearTimeout(t);
    }, [pendingServices, pendingDateStr]);

    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedFacilityDetails, setSelectedFacilityDetails] = useState(null);
    const [issueOpen, setIssueOpen] = useState(false);

    const handleSelect = (facilityId) => {
        setSelectedFacilities((prev) =>
            prev.includes(facilityId) ? prev.filter((id) => id !== facilityId) : [...prev, facilityId]
        );
    };

    const handleRowClick = (facility) => {
        setSelectedFacilityDetails(facility);
        setDetailsOpen(true);
    };

    const selectedFacilityObjects = (facilities || []).filter((f) =>
        selectedFacilities.includes(f.facility_unique_id)
    );

    // Block facilities that already have an issue (any f.warning present)
    const eligibleForIssue = selectedFacilityObjects.filter((f) => !f?.warning);
    const excludedForIssue = selectedFacilityObjects.filter((f) => !!f?.warning);

    // enable Report button only if at least one eligible
    const canReportIssue = eligibleForIssue.length > 0;

    const serviceHeaders = useMemo(
        () => (Array.isArray(services) && services.length > 0 ? services : ALL_SERVICE_HEADERS),
        [services]
    );

    const getLatestInstance = (serviceInstances) => {
        if (!Array.isArray(serviceInstances) || serviceInstances.length === 0) return null;
        return serviceInstances.slice().sort(
            (a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0)
        )[0];
    };

    const renderStatusIcon = (inst) => {
        if (!inst) return null;
        let raw = (inst.status ?? inst.service_status ?? "").toString().toLowerCase().trim();
        if (!raw) {
            if (inst.start_date && !inst.stop_date) raw = "active";
            else if (inst.start_date && inst.stop_date) raw = "ended";
        }
        const labelBits = [
            raw || "unknown",
            inst.start_date ? `start ${dayjs(inst.start_date).format("YYYY-MM-DD")}` : null,
            inst.stop_date ? `stop ${dayjs(inst.stop_date).format("YYYY-MM-DD")}` : null,
        ].filter(Boolean);
        const title = labelBits.join(" · ");

        switch (raw) {
            case "active":
                return <Tooltip title={title}><CheckCircleOutlineIcon fontSize="small" color="success" /></Tooltip>;
            case "ended":
                return <Tooltip title={title}><StopCircleIcon fontSize="small" color="error" /></Tooltip>;
            case "on hold":
            case "on_hold":
            case "hold":
                return <Tooltip title={title}><PauseCircleOutlineIcon fontSize="small" color="warning" /></Tooltip>;
            case "pending approval":
            case "pending_approval":
            case "pending":
                return <Tooltip title={title}><PendingActionsIcon fontSize="small" color="info" /></Tooltip>;
            case "failed launch":
            case "failed_launch":
            case "failed":
                return <Tooltip title={title}><HighlightOffIcon fontSize="small" color="error" /></Tooltip>;
            default:
                return <Tooltip title={title || "unknown"}><HelpOutlineIcon fontSize="small" /></Tooltip>;
        }
    };

    // Keep all rows visible except ones with warning.hide === true
    const visibleFacilities = useMemo(
        () => (facilities || []).filter((f) => !(f?.warning && f.warning?.hide === true)),
        [facilities]
    );

    if (loading) {
        return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
    }
    if (error) {
        return <Box mt={4}><Alert severity="error">{error}</Alert></Box>;
    }

// page from backend is 1-based; MUI wants 0-based
    const zeroBasedPage = Math.max(0, (pagination?.page || 1) - 1);

// use total from backend so MUI knows how many pages exist
    const totalCount =
        pagination?.total ??
        pagination?.count ??
        visibleFacilities.length;

// current rows per page from backend
    const rowsPerPage =
        pagination?.pageSize ??
        pagination?.limit ??
        100;


    return (
        <Box p={3} sx={{ pt: 2 }}>
            <Box
                sx={{
                    position: "sticky", top: 0, zIndex: 10, bgcolor: "background.paper",
                    pb: 1, pt: 1, borderBottom: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography variant="h5" sx={{ flexGrow: 1 }}>Facility Management</Typography>

                    {/* Report Issue to the LEFT */}
                    <Tooltip
                        title={
                            canReportIssue
                                ? `Report an issue for ${eligibleForIssue.length} selected `
                                : selectedFacilityObjects.length === 0
                                    ? "Select at least one facility"
                                    : "All selected facilities already have an issue"
                        }
                    >
            <span>
              <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ReportProblemOutlinedIcon />}
                  onClick={() => setIssueOpen(true)}
                  disabled={!canReportIssue}
              >
                Report Issue
              </Button>
            </span>
                    </Tooltip>

                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setModalOpen(true)}
                        disabled={selectedFacilities.length === 0}
                    >
                        Process Selected
                    </Button>
                </Box>

                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Autocomplete
                        multiple
                        options={SERVICE_OPTIONS}
                        value={pendingServices}
                        onChange={(_, val) => setPendingServices(val || [])}
                        renderInput={(params) => (
                            <TextField {...params} variant="standard" label="Services" placeholder="Select…" />
                        )}
                        sx={{ minWidth: 300 }}
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date Active"
                            value={pendingDateStr ? dayjs(pendingDateStr) : null}
                            onChange={(v) => setPendingDateStr(v && dayjs(v).isValid() ? dayjs(v).format("YYYY-MM-DD") : "")}
                            format="YYYY-MM-DD"
                            slotProps={{ textField: { variant: "standard", sx: { minWidth: 180 } } }}
                        />
                    </LocalizationProvider>

                    <Button variant="contained" onClick={() => { onChangeServices?.(pendingServices); onChangeDate?.(pendingDateStr); onApply?.(); }}>
                        Apply
                    </Button>

                    <Button variant="text" onClick={() => setPendingDateStr("")} disabled={pendingDateStr === ""}>
                        Clear Date
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={zeroBasedPage}
                        onPageChange={(_, newZeroPage) => onPageChange?.(newZeroPage + 1)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
                        rowsPerPageOptions={[25, 50, 100, 200, 500]}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
                        sx={{ p: 0, ".MuiTablePagination-toolbar": { pl: 0 } }}
                    />
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell><strong>Corp Name</strong></TableCell>
                            <TableCell><strong>Facility Name</strong></TableCell>
                            <TableCell><strong>City</strong></TableCell>
                            <TableCell><strong>State</strong></TableCell>
                            <TableCell><strong>Provider Number</strong></TableCell>
                            {serviceHeaders.map((serviceName) => (
                                <TableCell key={serviceName} align="center"><strong>{serviceName}</strong></TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleFacilities.map((facility) => {
                            const isChecked = selectedFacilities.includes(facility.facility_unique_id);
                            const hasWarning = !!facility?.warning;
                            const warningTitle = facility?.warning?.title || "Warning";
                            const warningNotes = facility?.warning?.notes || "";

                            return (
                                <TableRow
                                    key={facility.facility_unique_id}
                                    hover
                                    onClick={(e) => { if (e.target.type !== "checkbox") handleRowClick(facility); }}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <TableCell>
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={() => handleSelect(facility.facility_unique_id)}
                                        />
                                    </TableCell>

                                    <TableCell>{facility.corp_name}</TableCell>

                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <span>{facility.facility_name}</span>

                                            {/* Existing warning */}
                                            {hasWarning && (
                                                <Tooltip title={warningNotes ? `${warningTitle}: ${warningNotes}` : warningTitle}>
                                                    <WarningAmberIcon
                                                        fontSize="small"
                                                        color="warning"
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ cursor: "default" }}
                                                    />
                                                </Tooltip>
                                            )}

                                            {/* NEW: possible corp change */}
                                            {!!facility?.possible_corp_change && (
                                                <Tooltip title={buildCorpChangeTooltip(facility.possible_corp_change)}>
                                                    <CompareArrowsIcon
                                                        fontSize="small"
                                                        color="info"
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ cursor: "default" }}
                                                    />
                                                </Tooltip>
                                            )}

                                            {/* NEW: deactivated api */}
                                            {!!facility?.deactivated_api && (
                                                <Tooltip title={buildDeactivatedApiTooltip(facility.deactivated_api)}>
                                                    <BlockIcon
                                                        fontSize="small"
                                                        color="error"
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ cursor: "default" }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>


                                    <TableCell>{facility.city}</TableCell>
                                    <TableCell>{facility.state}</TableCell>
                                    <TableCell>{facility.source_id || "N/A"}</TableCell>

                                    {serviceHeaders.map((serviceName) => {
                                        const serviceInstances = (facility.services || []).filter((s) => s.service_name === serviceName);
                                        const latestInstance = getLatestInstance(serviceInstances);
                                        return (
                                            <TableCell key={serviceName} align="center">
                                                {renderStatusIcon(latestInstance)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Existing modals */}
            <ProcessSelectedModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                selectedFacilities={selectedFacilityObjects}
                onUpdateFacilities={reloadFacilities}
            />

            <FacilityServiceDetailsModal
                open={detailsOpen}
                onClose={() => { setDetailsOpen(false); setSelectedFacilityDetails(null); }}
                facility={selectedFacilityDetails}
            />

            {/* New multi-facility issue modal */}
            <ReportIssueModal
                open={issueOpen}
                onClose={() => setIssueOpen(false)}
                facilities={eligibleForIssue}
                excluded={excludedForIssue}
                onSubmitted={reloadFacilities}
            />
        </Box>
    );
};

export default FacilityManagementView;
